import type { Vec2 } from './types';

export const TAU = Math.PI * 2;

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const invLerp = (a: number, b: number, v: number): number => (b === a ? 0 : (v - a) / (b - a));

export const smoothstep = (t: number): number => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/** -PI..PI közé hajtott szögkülönbség (a-b) */
export const angleDiff = (a: number, b: number): number => {
  let d = (a - b) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
};

export const angleLerp = (a: number, b: number, t: number): number => a + angleDiff(b, a) * t;

export const vec = (x = 0, y = 0): Vec2 => ({ x, y });

export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });

export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });

export const scale = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k });

export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;

export const len = (a: Vec2): number => Math.hypot(a.x, a.y);

export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

export const dist2 = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

export const norm = (a: Vec2): Vec2 => {
  const l = Math.hypot(a.x, a.y) || 1;
  return { x: a.x / l, y: a.y / l };
};

export const rotate = (a: Vec2, ang: number): Vec2 => {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
};

export const fromAngle = (ang: number, r = 1): Vec2 => ({ x: Math.cos(ang) * r, y: Math.sin(ang) * r });

/** megközelítés (frame-rate független simítás) */
export const damp = (current: number, target: number, lambda: number, dt: number): number =>
  lerp(current, target, 1 - Math.exp(-lambda * dt));

export const dampVec = (current: Vec2, target: Vec2, lambda: number, dt: number): Vec2 => ({
  x: damp(current.x, target.x, lambda, dt),
  y: damp(current.y, target.y, lambda, dt),
});

/** 1,3,5 -> a legközelebbi "szép" érték, csak segéd */
export const sign = (v: number): number => (v > 0 ? 1 : v < 0 ? -1 : 0);

export const kmh = (pxPerSec: number): number => pxPerSec * 0.3;

export const mixColor = (a: string, b: string, t: number): string => {
  const pa = parseHex(a);
  const pb = parseHex(b);
  const c = [0, 1, 2].map((i) => Math.round(lerp(pa[i], pb[i], t)));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

const parseHex = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((ch) => ch + ch).join('');
  const n = parseInt(h.slice(0, 6), 16);
  if (Number.isNaN(n)) return [255, 255, 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** determinisztikus, gyors 32 bites hash – pálya-dekorációhoz */
export const hash01 = (n: number): number => {
  let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
};
